import { X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties, ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  buildDialogBackdropVariants,
  buildDialogPanelVariants,
  useYstReducedMotion,
} from '../motion/ystMotion';

const DEFAULT_VIEWPORT_INSET_PX = 16;

type DialogTestIds = {
  root: string | null;
  layer: string | null;
  backdrop: string | null;
  body: string | null;
  footer: string | null;
};

type ShellInsets = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

export type AppDialogFrameProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  testId?: string;
  closeLabel?: string;
  closeIcon?: ReactNode;
  dismissOnBackdrop?: boolean;
  panelClassName?: string;
  headerClassName?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  maxWidthClassName?: string;
  bodyPaddingClassName?: string;
  footerPaddingClassName?: string;
  bodyTestId?: string;
  footerTestId?: string;
  headerMode?: 'standard' | 'none';
  insetMode?: 'shell-content' | 'viewport';
  backdropInsetMode?: 'shell-content' | 'viewport';
  layerZIndex?: CSSProperties['zIndex'];
};

const isHTMLElement = (value: Element | null): value is HTMLElement => value instanceof HTMLElement;

const findShellContentRoot = (): HTMLElement | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  const contentRoot = document.querySelector<HTMLElement>(
    '[data-testid="app-shell-content-root"], [data-testid="parent-shell-content-root"]',
  );
  return isHTMLElement(contentRoot) ? contentRoot : null;
};

const readViewportInsetFloor = (contentRoot: HTMLElement | null): number => {
  if (!contentRoot) {
    return DEFAULT_VIEWPORT_INSET_PX;
  }

  const rawValue = Number(contentRoot.dataset.shellContentMinInset ?? DEFAULT_VIEWPORT_INSET_PX);
  return Number.isFinite(rawValue) && rawValue > 0 ? rawValue : DEFAULT_VIEWPORT_INSET_PX;
};

const computeShellInsets = (insetMode: AppDialogFrameProps['insetMode'] = 'shell-content'): ShellInsets => {
  if (typeof window === 'undefined') {
    return {
      left: DEFAULT_VIEWPORT_INSET_PX,
      right: DEFAULT_VIEWPORT_INSET_PX,
      top: DEFAULT_VIEWPORT_INSET_PX,
      bottom: DEFAULT_VIEWPORT_INSET_PX,
    };
  }

  const contentRoot = findShellContentRoot();
  const insetFloor = readViewportInsetFloor(contentRoot);

  if (insetMode === 'viewport') {
    return {
      left: insetFloor,
      right: insetFloor,
      top: insetFloor,
      bottom: insetFloor,
    };
  }

  if (!contentRoot) {
    return {
      left: insetFloor,
      right: insetFloor,
      top: insetFloor,
      bottom: insetFloor,
    };
  }

  const rect = contentRoot.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return {
      left: insetFloor,
      right: insetFloor,
      top: insetFloor,
      bottom: insetFloor,
    };
  }

  return {
    left: Math.max(insetFloor, Math.round(rect.left)),
    right: Math.max(insetFloor, Math.round(window.innerWidth - rect.right)),
    top: insetFloor,
    bottom: insetFloor,
  };
};

const buildDialogTestIds = (testId: string | undefined): DialogTestIds => {
  const root = String(testId ?? '').trim();
  if (!root) {
    return {
      root: null,
      layer: null,
      backdrop: null,
      body: null,
      footer: null,
    };
  }

  return {
    root,
    layer: `${root}-layer`,
    backdrop: `${root}-backdrop`,
    body: `${root}-body`,
    footer: `${root}-footer`,
  };
};

export const AppDialogFrame = ({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  testId,
  closeLabel,
  closeIcon,
  dismissOnBackdrop = true,
  panelClassName = '',
  headerClassName = '',
  titleClassName = '',
  descriptionClassName = '',
  bodyClassName = '',
  footerClassName = '',
  maxWidthClassName = 'max-w-lg',
  bodyPaddingClassName = 'px-6 py-5',
  footerPaddingClassName = 'px-6 py-4',
  bodyTestId,
  footerTestId,
  headerMode = 'standard',
  insetMode = 'shell-content',
  backdropInsetMode = 'viewport',
  layerZIndex,
}: AppDialogFrameProps) => {
  const [shellInsets, setShellInsets] = useState<ShellInsets>(() => computeShellInsets(insetMode));
  const [backdropInsets, setBackdropInsets] = useState<ShellInsets>(() => computeShellInsets(backdropInsetMode));
  const testIds = useMemo(() => buildDialogTestIds(testId), [testId]);
  const reducedMotion = useYstReducedMotion();
  const labelledBy = useMemo(() => {
    if (testIds.root) {
      return `${testIds.root}-title`;
    }
    return undefined;
  }, [testIds.root]);

  useEffect(() => {
    if (!open || typeof document === 'undefined') {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open || typeof window === 'undefined') {
      return undefined;
    }

    const syncInsets = () => {
      setShellInsets(computeShellInsets(insetMode));
      setBackdropInsets(computeShellInsets(backdropInsetMode));
    };

    const contentRoot = findShellContentRoot();
    const resizeObserver = typeof ResizeObserver !== 'undefined' && contentRoot
      ? new ResizeObserver(syncInsets)
      : null;

    syncInsets();
    resizeObserver?.observe(contentRoot as HTMLElement);
    window.addEventListener('resize', syncInsets);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', syncInsets);
    };
  }, [backdropInsetMode, insetMode, open]);

  useEffect(() => {
    if (!open || typeof window === 'undefined') {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, open]);

  if (typeof document === 'undefined') {
    return null;
  }

  const layerStyle: CSSProperties = {
    zIndex: layerZIndex ?? 'var(--overlay-z-modal, 1400)',
  };
  const centeringStyle: CSSProperties = {
    paddingLeft: shellInsets.left,
    paddingRight: shellInsets.right,
    paddingTop: shellInsets.top,
    paddingBottom: shellInsets.bottom,
  };
  const backdropStyle: CSSProperties | undefined = backdropInsetMode === 'shell-content'
    ? {
      left: backdropInsets.left,
      right: backdropInsets.right,
      top: backdropInsets.top,
      bottom: backdropInsets.bottom,
    }
    : undefined;

  const backdropVariants = buildDialogBackdropVariants(reducedMotion);
  const panelVariants = buildDialogPanelVariants(reducedMotion);

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.section
          key={testIds.layer ?? testIds.root ?? 'app-dialog-frame'}
          data-testid={testIds.layer ?? undefined}
          className="fixed inset-0 overflow-y-auto pointer-events-none"
          style={layerStyle}
          role="presentation"
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <motion.button
            type="button"
            data-testid={testIds.backdrop ?? undefined}
            className={backdropInsetMode === 'shell-content' ? 'fixed bg-black/35' : 'fixed inset-0 bg-black/35'}
            style={backdropStyle}
            onClick={dismissOnBackdrop ? onClose : undefined}
            aria-label={typeof title === 'string' && title.trim().length > 0 ? `Close ${title}` : 'Close dialog'}
            variants={backdropVariants}
          />

          <div
            className="pointer-events-none relative flex min-h-full items-start justify-center sm:items-center"
            style={centeringStyle}
          >
            <motion.article
              data-testid={testIds.root ?? undefined}
              className={[
                'pointer-events-auto relative flex w-full max-h-[calc(100dvh-2rem)] flex-col overflow-hidden rounded-2xl border border-border bg-surface-light shadow-2xl',
                maxWidthClassName,
                panelClassName,
              ].filter(Boolean).join(' ')}
              role="dialog"
              aria-modal="true"
              aria-labelledby={labelledBy}
              variants={panelVariants}
            >
              {headerMode === 'standard' ? (
                <header
                  className={[
                    'flex shrink-0 items-start justify-between gap-4 border-b border-border bg-white px-6 py-4',
                    headerClassName,
                  ].filter(Boolean).join(' ')}
                >
                  <div className="min-w-0 flex-1">
                    <h2
                      id={labelledBy}
                      className={['font-heading text-lg font-semibold text-irongrey', titleClassName].filter(Boolean).join(' ')}
                    >
                      {title}
                    </h2>
                    {description ? (
                      <p className={['mt-1 text-sm text-irongrey/70', descriptionClassName].filter(Boolean).join(' ')}>
                        {description}
                      </p>
                    ) : null}
                  </div>

                  <button
                    type="button"
                    onClick={onClose}
                    className="yst-button-secondary yst-motion-color rounded-lg p-1.5 text-irongrey/50 shadow-none"
                    aria-label={closeLabel ?? (typeof title === 'string' && title.trim().length > 0 ? `Dismiss ${title}` : 'Dismiss dialog')}
                  >
                    {closeIcon ?? <X size={18} />}
                  </button>
                </header>
              ) : labelledBy ? (
                <h2 id={labelledBy} className="sr-only">
                  {title}
                </h2>
              ) : null}

              <div
                data-testid={bodyTestId ?? testIds.body ?? undefined}
                className={[
                  'min-h-0 flex-1 overflow-y-auto',
                  bodyPaddingClassName,
                  bodyClassName,
                ].filter(Boolean).join(' ')}
              >
                {children}
              </div>

              {footer ? (
                <footer
                  data-testid={footerTestId ?? testIds.footer ?? undefined}
                  className={[
                    'flex shrink-0 items-center justify-end gap-2 border-t border-border bg-muted/60',
                    footerPaddingClassName,
                    footerClassName,
                  ].filter(Boolean).join(' ')}
                >
                  {footer}
                </footer>
              ) : null}
            </motion.article>
          </div>
        </motion.section>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
};
